<script setup lang="ts">
import { ConfirmationPopup, formatDateOnly } from '@metapic-apps/shared'
import { computed, ref } from 'vue'

import UrlMatchRuleForm from './UrlMatchRuleForm/UrlMatchRuleForm.vue'

import { useDeleteUrlMatchingRule, useGetUrlMatchingRules } from '@/api'
import AddPlusIcon from '@/assets/icons/add-plus.svg?component'
import DeleteIcon from '@/assets/icons/delete.svg?component'
import EditIcon from '@/assets/icons/edit.svg?component'
import { notification } from '@/lib/helpers'
import type { UrlMatchingRuleType } from '@/lib/types/url-matching-rules.types'
import { BoxComponent, ButtonComponent, FlexComponent, LoadingComponent, TagComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  advertiserId: number
}>()
const rulesLimit = 20
const isDeletePopupOpen = ref(false)
const currentItem = ref<UrlMatchingRuleType | null>(null)
const isUrlMatchRuleFormOpen = ref(false)
const { data, isLoading, getUrlMatchingRules } = useGetUrlMatchingRules(props.advertiserId)
const { deleteUrlMatchingRule, isLoading: isDeleting, error: deleteError } = useDeleteUrlMatchingRule()
const hasLimitReached = computed(() => {
  return data.value ? data.value.length >= rulesLimit : false
})

const deleteRule = async () => {
  if (!currentItem.value) {
    return
  }
  await deleteUrlMatchingRule(props.advertiserId, currentItem.value.id)
  isDeletePopupOpen.value = false

  if (!deleteError.value) {
    currentItem.value = null
    await getUrlMatchingRules()
    notification.success('The URL matching rule was deleted successfully')
  }
}
const openDeleteRulePopup = (value: UrlMatchingRuleType) => {
  currentItem.value = value
  isDeletePopupOpen.value = true
}
const openEditRulePopup = (value: UrlMatchingRuleType) => {
  currentItem.value = value
  isUrlMatchRuleFormOpen.value = true
}
</script>
<template>
  <LoadingComponent v-if="isLoading" :size="64" />
  <BoxComponent v-else>
    <BoxComponent v-if="data?.length" :mb="24">
      <FlexComponent justify="flex-end">
        <el-tooltip :disabled="!hasLimitReached" placement="bottom-end" effect="light" :show-arrow="false">
          <ButtonComponent :is-disabled="hasLimitReached" variant="primary" @click="isUrlMatchRuleFormOpen = true">
            <template #iconLeft>
              <el-icon :size="24" color="var(--inverted)">
                <AddPlusIcon />
              </el-icon>
            </template>
            Add Rule
          </ButtonComponent>
          <template #content>
            <TypographyComponent variant="title-medium">The limit has been reached.</TypographyComponent>
            <TypographyComponent variant="body-medium">You can add up to {{ rulesLimit }} URL matching rules.</TypographyComponent>
          </template>
        </el-tooltip>
      </FlexComponent>
    </BoxComponent>
    <FlexComponent v-if="data?.length" direction="column" :gap="8">
      <BoxComponent v-for="(item, index) in data" :key="index" :p="16" w="100%" class="advertiser-details-url-matching-rules__list-item">
        <FlexComponent :gap="8" justify="space-between" align="center">
          <BoxComponent w="60%" overflow-hidden>
            <FlexComponent :gap="16" align="center">
              <TagComponent v-if="item.type === 'allow'" variant="success">Allowed</TagComponent>
              <TagComponent v-else variant="danger">Blocked</TagComponent>
              <template v-if="item.url">
                <TypographyComponent variant="body-large">URL:&nbsp;</TypographyComponent>
                <el-tooltip placement="bottom-start" effect="light" :show-arrow="false">
                  <TypographyComponent variant="title-large" text-truncate white-space="nowrap"> {{ item.url }} </TypographyComponent>
                  <template #content>
                    <TypographyComponent variant="body-small">
                      {{ item.url }}
                    </TypographyComponent>
                  </template>
                </el-tooltip>
              </template>
              <template v-if="item.keyword">
                <TypographyComponent variant="body-large">Keyword:&nbsp;</TypographyComponent>
                <el-tooltip placement="bottom-start" effect="light" :show-arrow="false">
                  <TypographyComponent variant="title-large" text-truncate white-space="nowrap"> {{ item.keyword }} </TypographyComponent>
                  <template #content>
                    <TypographyComponent variant="body-small">
                      {{ item.keyword }}
                    </TypographyComponent>
                  </template>
                </el-tooltip>
              </template>
              <template v-if="item.isMain">
                <el-tooltip placement="bottom-start" effect="light" :show-arrow="false">
                  <BoxComponent :p="8" class="advertiser-details-url-matching-rules__background">
                    <TypographyComponent variant="label-medium" text-align="center">Main</TypographyComponent>
                  </BoxComponent>

                  <template #content>
                    <TypographyComponent variant="body-small">
                      This is the main URL that creators will be redirected to when clicking the advertiser card.
                    </TypographyComponent>
                  </template>
                </el-tooltip>
              </template>
            </FlexComponent>
          </BoxComponent>
          <BoxComponent w="30%">
            <FlexComponent direction="column" justify="center">
              <TypographyComponent variant="label-medium" text-align="center">Last updated</TypographyComponent>
              <TypographyComponent variant="body-medium" text-align="center">
                <span>{{ formatDateOnly(item.updatedAt) }}</span>
                <span v-if="item.updatedBy">, {{ item.updatedBy?.name }} </span>
              </TypographyComponent>
            </FlexComponent>
          </BoxComponent>
          <BoxComponent w="10%">
            <FlexComponent>
              <ButtonComponent
                variant="transparent"
                is-icon
                icon-color="var(--primary)"
                class="advertiser-details-url-matching-rules__button"
                @click="openEditRulePopup(item)"
              >
                <EditIcon />
              </ButtonComponent>
              <ButtonComponent
                variant="transparent"
                is-icon
                icon-color="var(--primary)"
                class="advertiser-details-url-matching-rules__button advertiser-details-url-matching-rules__button-danger"
                @click="openDeleteRulePopup(item)"
              >
                <DeleteIcon />
              </ButtonComponent>
            </FlexComponent>
          </BoxComponent>
        </FlexComponent>
      </BoxComponent>
    </FlexComponent>
    <BoxComponent v-else :pl="16" :pr="16" :pt="48" :pb="48">
      <FlexComponent align="center" direction="column" :gap="24">
        <TypographyComponent variant="body-large" color="var(--secondary-48)" text-align="center">No rules yet.</TypographyComponent>
        <ButtonComponent variant="primary" @click="isUrlMatchRuleFormOpen = true">
          <template #iconLeft>
            <el-icon :size="24" color="var(--inverted)">
              <AddPlusIcon />
            </el-icon>
          </template>
          Add Rule
        </ButtonComponent>
      </FlexComponent>
    </BoxComponent>
  </BoxComponent>
  <ConfirmationPopup
    v-model="isDeletePopupOpen"
    :is-loading="isDeleting"
    title="Delete rule"
    description="Are you sure you want to delete this rule? This action cannot be undone."
    @on-submit="deleteRule"
  >
    <BoxComponent v-if="currentItem" :mb="24" :p="16" class="advertiser-details-url-matching-rules__background" overflow-hidden>
      <FlexComponent :gap="16" align="center">
        <TagComponent v-if="currentItem.type === 'allow'" variant="success">Allowed</TagComponent>
        <TagComponent v-else variant="danger">Blocked</TagComponent>
        <template v-if="currentItem.url">
          <TypographyComponent variant="body-large">URL:&nbsp;</TypographyComponent>
          <TypographyComponent variant="title-large" text-truncate white-space="nowrap"> {{ currentItem.url }} </TypographyComponent>
        </template>
        <template v-if="currentItem.keyword">
          <TypographyComponent variant="body-large">Keyword:&nbsp;</TypographyComponent>
          <TypographyComponent variant="title-large" text-truncate white-space="nowrap"> {{ currentItem.keyword }} </TypographyComponent>
        </template>
      </FlexComponent>
    </BoxComponent>
  </ConfirmationPopup>
  <UrlMatchRuleForm
    v-if="isUrlMatchRuleFormOpen"
    v-model="isUrlMatchRuleFormOpen"
    v-model:rule="currentItem"
    :advertiser-id="advertiserId"
    @handle-submit-success="() => getUrlMatchingRules()"
  />
</template>
<style lang="scss" scoped>
:deep(.el-icon) {
  path {
    fill: currentColor;
  }
}

.advertiser-details-url-matching-rules__list-item {
  border-radius: 16px;
  border: 1px solid var(--button-hover);
  background: var(--inverted);

  &:hover {
    background: var(--secondary-16);
  }
}

.advertiser-details-url-matching-rules__button {
  &:hover {
    border-radius: 50%;
    background: var(--button-hover);
  }
  &-danger:hover {
    :deep(.el-icon) {
      path {
        fill: var(--error);
      }
    }
  }
}

.advertiser-details-url-matching-rules__background {
  border-radius: 16px;
  background: var(--secondary-8);
}
</style>
